import {
  CONSTANTS,
  REVENU_FIELDS,
  CHARGES_FIXES_FIELDS,
  CREDITS_FIELDS,
  DEPENSES_VARIABLES_FIELDS,
} from "./constants";

const sumFields = (obj = {}, fields = []) =>
  fields.reduce((acc, f) => acc + (Number(obj?.[f]) || 0), 0);

export const computeTotals = (state) => {
  const revenus = sumFields(state.revenus, REVENU_FIELDS);
  const chargesFixes = sumFields(state.depenses, CHARGES_FIXES_FIELDS);
  const credits = sumFields(state.depenses, CREDITS_FIELDS);
  const variables = sumFields(state.depenses, DEPENSES_VARIABLES_FIELDS);
  const depensesTotales = chargesFixes + credits + variables;
  return { revenus, chargesFixes, credits, variables, depensesTotales };
};

export const computeIndicators = (state) => {
  const totals = computeTotals(state);
  const { revenus, chargesFixes, credits, variables, depensesTotales } = totals;

  const resteAVivre = revenus - depensesTotales;
  const tauxEndettement = revenus > 0 ? credits / revenus : 0;
  const poidsChargesFixes = revenus > 0 ? chargesFixes / revenus : 0;
  const poidsVariables = revenus > 0 ? variables / revenus : 0;
  const mensualiteMax = revenus * CONSTANTS.TAUX_ENDETTEMENT_MAX;
  const mensualiteDispo = Math.max(0, mensualiteMax - credits);
  const capaciteEmprunt = mensualiteDispo * CONSTANTS.DUREE_EMPRUNT_MOIS;

  return {
    ...totals,
    resteAVivre,
    tauxEndettement,
    poidsChargesFixes,
    poidsVariables,
    mensualiteMax,
    mensualiteDispo,
    capaciteEmprunt,
  };
};

export const computeDiagnostic = (ind) => {
  const endettementOk = ind.tauxEndettement <= CONSTANTS.TAUX_ENDETTEMENT_MAX;
  const ravOk = ind.resteAVivre >= CONSTANTS.RESTE_A_VIVRE_MIN;

  let global;
  if (!endettementOk) global = "fragile";
  else if (!ravOk) global = "surveiller";
  else global = "saine";

  return {
    endettement: endettementOk ? "ok" : "alerte",
    rav: ravOk ? "ok" : "alerte",
    budget: endettementOk && ravOk ? "ok" : "optim",
    global,
  };
};

export const computeOptimisation = (ind, tauxVariables, tauxFixes) => {
  const economiesVariables = ind.variables * tauxVariables;
  const economiesFixes = ind.chargesFixes * tauxFixes;
  const gainMensuel = economiesVariables + economiesFixes;
  const nouveauRav = ind.resteAVivre + gainMensuel;
  const epargneAnnuelle = gainMensuel * 12;
  const nouveauTaux = ind.tauxEndettement; // crédits inchangés
  return {
    economiesVariables,
    economiesFixes,
    gainMensuel,
    nouveauRav,
    epargneAnnuelle,
    nouveauTaux,
  };
};

export const computeRecommendations = (ind) => {
  const items = [];
  const push = (id, level, labelKey, msgKey) => items.push({ id, level, labelKey, msgKey });

  // Endettement
  if (ind.tauxEndettement > CONSTANTS.TAUX_ENDETTEMENT_MAX) {
    push("endettement", "alerte", "endettement", "endettementAlerte");
  } else {
    push("endettement", "info", "endettement", "endettementOk");
  }

  // RAV
  if (ind.resteAVivre < CONSTANTS.RESTE_A_VIVRE_MIN) {
    push("rav", "alerte", "rav", "ravAlerte");
  } else {
    push("rav", "info", "rav", "ravOk");
  }

  // Variables
  if (ind.poidsVariables > CONSTANTS.POIDS_VARIABLES_ALERTE) {
    push("variables", "alerte", "variables", "variablesAlerte");
  } else {
    push("variables", "info", "variables", "variablesOk");
  }

  // Fixes
  if (ind.poidsChargesFixes > CONSTANTS.POIDS_CHARGES_ALERTE) {
    push("fixes", "alerte", "fixes", "fixesAlerte");
  } else {
    push("fixes", "info", "fixes", "fixesOk");
  }

  // Rachat de crédits (opportunité)
  if (ind.tauxEndettement > CONSTANTS.TAUX_ENDETTEMENT_ALERTE) {
    push("rachat", "opportunite", "rachat", "rachatOpp");
  } else {
    push("rachat", "info", "rachat", "rachatOk");
  }

  // Audit assurances
  if (ind.poidsChargesFixes > CONSTANTS.POIDS_CHARGES_OPPORTUNITE) {
    push("audit", "opportunite", "audit", "auditOpp");
  } else {
    push("audit", "info", "audit", "auditOk");
  }

  // Optimisation budget
  if (ind.poidsVariables > CONSTANTS.POIDS_VARIABLES_OPPORTUNITE) {
    push("budget", "opportunite", "budget", "budgetOpp");
  } else {
    push("budget", "info", "budget", "budgetOk");
  }

  return items;
};

export const moduleCompletion = (state) => {
  const hasClient = Object.values(state.client || {}).some((v) => v !== "" && v !== null && v !== undefined);
  const hasRevenus = sumFields(state.revenus, REVENU_FIELDS) > 0;
  const hasDepenses =
    sumFields(state.depenses, CHARGES_FIXES_FIELDS) +
      sumFields(state.depenses, CREDITS_FIELDS) +
      sumFields(state.depenses, DEPENSES_VARIABLES_FIELDS) >
    0;
  return {
    client: hasClient,
    revenus: hasRevenus,
    depenses: hasDepenses,
    analyse: hasRevenus || hasDepenses,
    optimisation: hasRevenus || hasDepenses,
    recommandations: hasRevenus || hasDepenses,
  };
};
